import './CopUserAccount.scss'

import { useEffect, useState } from 'react'

import Loader from '../../common/components/Loader'
import { ToastService } from '../../services/toast.service'
import CopSearchableDropdown from './CopSearchableDropdown'
import { COP_UA_DEFAULTS } from './CopUserAccount.constants'
import { useCopUserAccount } from './hooks/useCopUserAccount'
import { persistCustomerSelection } from '../SilentTokenProvider/userRoleRedirect'

const isAuthorMode = (): boolean => {
    try {
        const w = window as unknown as { Granite?: { author?: unknown } }
        return Boolean(w.Granite?.author)
    } catch {
        return false
    }
}

const SESSION_KEY_CUSTOMER_NAME = 'cop:customer-name'
const SESSION_KEY_CUSTOMER_ID = 'cop:customer-id'
const SESSION_KEY_CURRENT_USER = 'cop:current-user'

export type CopUserAccountProps = {
    switchAccountTitle?: string
    description?: string
    accountDropdownPlaceholder?: string
    confirmButtonLabel?: string
    confirmButtonUrl?: string
    cancelButtonLabel?: string
    cancelButtonUrl?: string
    alternativeAccessText?: string
    adminButtonLabel?: string
    adminButtonUrl?: string
    backgroundImageReference?: string

    selectionApiUrl?: string
}

const CopUserAccount = ({
    switchAccountTitle: title = COP_UA_DEFAULTS.SWITCH_ACCOUNT_TITLE,
    description: intro = COP_UA_DEFAULTS.DESCRIPTION,
    accountDropdownPlaceholder: placeholder = COP_UA_DEFAULTS.ACCOUNT_DROPDOWN_PLACEHOLDER,
    confirmButtonLabel: confirmLabel = COP_UA_DEFAULTS.CONFIRM_BUTTON_LABEL,
    confirmButtonUrl: confirmRedirectUrl = COP_UA_DEFAULTS.CONFIRM_BUTTON_URL,
    cancelButtonLabel: cancelLabel = COP_UA_DEFAULTS.CANCEL_BUTTON_LABEL,
    cancelButtonUrl: cancelUrl = COP_UA_DEFAULTS.CANCEL_BUTTON_URL,
    alternativeAccessText: orLabel = COP_UA_DEFAULTS.ALTERNATIVE_ACCESS_TEXT,
    adminButtonLabel: adminModeLabel = COP_UA_DEFAULTS.ADMIN_BUTTON_LABEL,
    adminButtonUrl: adminModeUrl = COP_UA_DEFAULTS.ADMIN_BUTTON_URL,
    backgroundImageReference: backgroundImageUrl = '',
    selectionApiUrl = '',
}: CopUserAccountProps) => {
    const { data, loading, searching, loadingMore, hasMore, error, selectedId, setSelectedId, submitting, confirm, setSearchTerm, loadMore } = useCopUserAccount(selectionApiUrl)
    const [selectedName, setSelectedName] = useState('')

    const isOnPostConfirmPage = (() => {
        if (!confirmRedirectUrl) return false
        try {
            const targetPathname = new URL(confirmRedirectUrl, window.location.href)
                .pathname.replace(/\/+$/, '')
            const currentPathname = window.location.pathname.replace(/\/+$/, '')
            return currentPathname === targetPathname
        } catch {
            return false
        }
    })()

    const hasSelectedAccount =
        !isAuthorMode() &&
        isOnPostConfirmPage &&
        Boolean(sessionStorage.getItem(SESSION_KEY_CUSTOMER_ID))

    const canCancel = Boolean(sessionStorage.getItem(SESSION_KEY_CUSTOMER_NAME))
    const isSystemAdmin = isAuthorMode() || (() => {
        try {
            const raw = sessionStorage.getItem(SESSION_KEY_CURRENT_USER)
            if (!raw) return false
            const user = JSON.parse(raw)
            return user?.isSystemAdmin === true
        } catch {
            return false
        }
    })()

    const isUserManager = (() => {
        try {
            const raw = sessionStorage.getItem(SESSION_KEY_CURRENT_USER)
            if (!raw) return false
            const user = JSON.parse(raw)
            return user?.isUserManager === true
        } catch {
            return false
        }
    })()

    useEffect(() => {
        if (error && !isAuthorMode()) {
            ToastService.error(COP_UA_DEFAULTS.API_ERROR_MESSAGE)
        }
    }, [error])

    const accounts = data?.value ?? []
    const canConfirm = Boolean(selectedId) && !submitting

    const handleConfirm = async () => {
        if (!canConfirm) return
        await confirm()
        persistCustomerSelection(selectedId, selectedName)
        if (confirmRedirectUrl) window.location.assign(confirmRedirectUrl)
    }

    const cancelRedirectUrl = (): void => {
        try {
            const pageTracker = sessionStorage.getItem('pageTracker');
            if (!pageTracker) {
                return;
            }
            const pageTrackerDetails = JSON.parse(pageTracker);
            const previousUrl = pageTrackerDetails?.previousUrl;
            if ((!previousUrl || typeof previousUrl !== 'string') && !sessionStorage.getItem(SESSION_KEY_CUSTOMER_NAME)) {
                return;
            } else {
                cancelUrl = previousUrl || cancelUrl;
            }
            window.location.assign(cancelUrl);
        } catch (error) {
            ToastService.error(`Failed to process pageTracker data: ${error instanceof Error ? error.message : String(error)}`)
        }
    };

    const selectAccount = (id: string, name: string) => {
        setSelectedId(id)
        setSelectedName(name)
    }

    const handleAdminModeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
        // Only default to NOSLA on fresh login — an existing selection must be preserved, not overwritten
        const hasExistingSelection = Boolean(sessionStorage.getItem(SESSION_KEY_CUSTOMER_ID))
        if (!hasExistingSelection) {
            persistCustomerSelection('NOSLA', 'No SLA')
        }
        if (adminModeUrl) window.location.assign(adminModeUrl)
    }

    if (hasSelectedAccount) return null

    return (
        <div
            className="cop-ua-overlay"
            style={backgroundImageUrl ? { '--cop-ua-bg-image': `url('${backgroundImageUrl}')` } as React.CSSProperties : undefined}
        >
            <dialog className="cop-ua" aria-labelledby="cop-ua-title" open>
                <h1 id="cop-ua-title" className="cop-ua__title">{title}</h1>
                <p className="cop-ua__intro">{intro}</p>

                {loading ? (
                    <div className="cop-ua__loader">
                        <Loader />
                    </div>
                ) : (
                    <>
                        <CopSearchableDropdown
                            accounts={accounts}
                            selectedId={selectedId}
                            selectedName={selectedName}
                            loading={loading}
                            searching={searching}
                            loadingMore={loadingMore}
                            hasMore={hasMore}
                            placeholder={placeholder}
                            searchPlaceholder={COP_UA_DEFAULTS.SEARCH_PLACEHOLDER}
                            showNoSla={isSystemAdmin}
                            onSelect={selectAccount}
                            onSearchChange={setSearchTerm}
                            onLoadMore={loadMore}
                        />

                        <div className="cop-ua__actions">
                            <button
                                type="button"
                                className="cop-ua__btn cop-ua__btn--confirm"
                                onClick={handleConfirm}
                                disabled={!canConfirm}
                            >
                                {submitting ? 'Submitting…' : confirmLabel}
                            </button>
                            <button
                                type="button"
                                className="cop-ua__btn cop-ua__btn--cancel"
                                onClick={cancelRedirectUrl}
                                disabled={!canCancel}
                            >
                                {cancelLabel}
                            </button>
                        </div>
                    </>
                )}

                {(isSystemAdmin || isUserManager) && (
                    <>
                        <hr className="cop-ua__divider" />
                        <p className="cop-ua__or">{orLabel}</p>

                        <a className="cop-ua__btn cop-ua__btn--admin" href={adminModeUrl} onClick={handleAdminModeClick}>
                            {adminModeLabel}
                        </a>
                    </>
                )}
            </dialog>
        </div>
    )
}

export default CopUserAccount